import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { greenColor } from '../../../config/colors';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatNumericInput, parseCurrencyInput } from '../../../utils';

const EMPTY_FORM = {
  name: '',
  interest_rate: '',
  iof: false,
  iof_rate: '',
  tac_rate: '',
  operational_fee: '',
  min_value: '',
  max_value: '',
};

const parseBR = (value) => {
  if (!value) return null;
  return parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

const SimulatorFormModal = ({ open, onClose, onSaved, initial = null }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEditing = Boolean(initial?.id);

  useEffect(() => {
    if (initial) {
      setForm({
        ...EMPTY_FORM,
        ...initial,
        iof: initial.iof ?? false,
        operational_fee: formatNumericInput(initial.operational_fee?.toString() || '', {
          type: 'currency',
          mode: 'display',
        }),
        min_value: formatNumericInput(initial.min_value?.toString() || '', {
          type: 'currency',
          mode: 'display',
        }),
        max_value: formatNumericInput(initial.max_value?.toString() || '', {
          type: 'currency',
          mode: 'display',
        }),
        interest_rate: formatNumericInput(initial.interest_rate?.toString() || '', {
          type: 'percent',
          mode: 'display',
        }),
        iof_rate: initial.iof
          ? formatNumericInput(initial.iof_rate?.toString() || '', {
              type: 'percent',
              mode: 'display',
            })
          : '',
        tac_rate: formatNumericInput(initial.tac_rate?.toString() || '', {
          type: 'percent',
          mode: 'display',
        }),
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [initial, open]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleIofChange = (e) => {
    const hasIof = e.target.value === 'true';
    setForm((prev) => ({
      ...prev,
      iof: hasIof,
      iof_rate: hasIof ? prev.iof_rate : '', // limpa iof_rate ao desativar
    }));
  };

  const handleFormattedChange = (fieldName, type) => (e) => {
    const formatted = formatNumericInput(e.target.value, { type });
    setForm((prev) => ({ ...prev, [fieldName]: formatted }));
  };

  const isEmpty = (v) => v === '' || v === null || v === undefined;

  const handleSave = async () => {
    if (
      !form.name ||
      isEmpty(form.interest_rate) ||
      (form.iof && isEmpty(form.iof_rate)) || // iof_rate só obrigatório se iof = true
      isEmpty(form.tac_rate) ||
      isEmpty(form.operational_fee) ||
      isEmpty(form.min_value) ||
      isEmpty(form.max_value)
    ) {
      toast.error(t('simulatorSuperAdmin.toasts.requiredFields'));
      return;
    }

    const payload = {
      ...form,
      interest_rate: parseBR(form.interest_rate),
      iof: form.iof,
      iof_rate: form.iof ? parseBR(form.iof_rate) : 0,
      tac_rate: parseBR(form.tac_rate),
      operational_fee: parseBR(form.operational_fee),
      min_value: parseBR(form.min_value),
      max_value: parseBR(form.max_value),
    };

    try {
      setSaving(true);
      if (isEditing) {
        await api.put(`/v1/simulators/${initial.id}`, payload);
        toast.success(t('simulatorSuperAdmin.toasts.updateSuccess'));
      } else {
        await api.post('/v1/simulators', payload);
        toast.success(t('simulatorSuperAdmin.toasts.createSuccess'));
      }
      onSaved();
      onClose();
    } catch (err) {
      const message =
        err?.response?.data?.errors?.[0]?.message ||
        err?.response?.data?.message ||
        t('simulatorSuperAdmin.toasts.saveError');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEditing
          ? t('simulatorSuperAdmin.formModal.editTitle')
          : t('simulatorSuperAdmin.formModal.newTitle')}
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}
      >
        <TextField
          label={t('simulatorSuperAdmin.formModal.nameLabel')}
          name="name"
          value={form.name}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          label={t('simulatorSuperAdmin.formModal.interestRateLabel')}
          name="interest_rate"
          value={form.interest_rate}
          onChange={handleFormattedChange('interest_rate', 'percent')}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          inputProps={{ maxLength: 5 }}
        />

        <TextField
          select
          label={t('simulatorSuperAdmin.formModal.applyIof')}
          name="iof"
          value={String(form.iof)}
          onChange={handleIofChange}
          fullWidth
        >
          <MenuItem value="false">{t('common.no')}</MenuItem>
          <MenuItem value="true">{t('common.yes')}</MenuItem>
        </TextField>

        {form.iof && (
          <TextField
            label={t('simulatorSuperAdmin.formModal.iofRateLabel')}
            name="iof_rate"
            value={form.iof_rate}
            onChange={handleFormattedChange('iof_rate', 'percent')}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ maxLength: 5 }}
          />
        )}

        <TextField
          label={t('simulatorSuperAdmin.formModal.tacRateLabel')}
          name="tac_rate"
          value={form.tac_rate}
          onChange={handleFormattedChange('tac_rate', 'percent')}
          fullWidth
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          inputProps={{ maxLength: 5 }}
        />

        <TextField
          label={t('simulatorSuperAdmin.formModal.operationalFeeLabel')}
          name="operational_fee"
          value={form.operational_fee}
          onChange={handleFormattedChange('operational_fee', 'currency')}
          fullWidth
          inputProps={{ inputMode: 'numeric', maxLength: 12 }}
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
        />

        <TextField
          label={t('simulatorSuperAdmin.formModal.minValueLabel')}
          name="min_value"
          value={form.min_value}
          onChange={handleFormattedChange('min_value', 'currency')}
          fullWidth
          inputProps={{ inputMode: 'numeric', maxLength: 12 }}
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
        />

        <TextField
          label={t('simulatorSuperAdmin.formModal.maxValueLabel')}
          name="max_value"
          value={form.max_value}
          onChange={handleFormattedChange('max_value', 'currency')}
          fullWidth
          inputProps={{ inputMode: 'numeric', maxLength: 12 }}
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{ bgcolor: greenColor, color: '#fff' }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimulatorFormModal;
